/**
 * Weather Widget component - Embedded version (no API key required)
 * Shows weather based on club location using wttr.in widget
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Cloud, Sun, CloudRain, Thermometer } from 'lucide-react';

export const WeatherWidget = ({ lat, lng, clubName, courtNumber, className = '' }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!lat || !lng) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Use wttr.in free weather API (no key required)
        const response = await fetch(
          `https://wttr.in/${lat},${lng}?format=j1`,
          { headers: { 'Accept': 'application/json' } }
        );
        
        if (!response.ok) throw new Error('Weather fetch failed');
        
        const data = await response.json();
        const current = data.current_condition?.[0];
        
        if (current) {
          setWeather({
            temp: parseInt(current.temp_C),
            humidity: parseInt(current.humidity),
            description: current.weatherDesc?.[0]?.value || 'N/A',
            feelsLike: parseInt(current.FeelsLikeC),
            windSpeed: parseInt(current.windspeedKmph),
            weatherCode: parseInt(current.weatherCode),
            uvIndex: parseInt(current.uvIndex || 0),
            precip: parseFloat(current.precipMM || 0)
          });
          setError(null);
        }
      } catch (err) {
        console.error('Weather error:', err);
        setError('Clima no disponible');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    
    // Refresh every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [lat, lng]);

  // Get weather icon based on code
  const getWeatherIcon = (code) => {
    if (!code) return Cloud;
    // Sunny
    if (code === 113) return Sun;
    // Rainy
    if ([176, 263, 266, 293, 296, 299, 302, 305, 308, 311, 314, 317, 353, 356, 359].includes(code)) return CloudRain;
    // Cloudy
    return Cloud;
  };

  if (loading) {
    return (
      <div className={`glass rounded-xl px-4 py-2.5 ${className}`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-full" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-muted rounded w-24" />
            <div className="h-3 bg-muted rounded w-16" />
          </div>
        </div>
      </div>
    );
  }

  const WeatherIcon = weather ? getWeatherIcon(weather.weatherCode) : Cloud;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass rounded-xl px-4 py-2.5 ${className}`}
      data-testid="weather-widget"
    >
      {/* Club/Court name */}
      {(clubName || courtNumber) && (
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-primary" />
          <span className="font-display font-bold text-lg">{clubName || 'Club'}</span>
          {courtNumber && (
            <>
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">Cancha {courtNumber}</span>
            </>
          )}
        </div>
      )}
      
      {/* Weather info */}
      {error || !weather ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Cloud className="w-4 h-4" />
          <span>{error || 'Sin datos de clima'}</span>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          {/* Temp + icon */}
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <WeatherIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <span className="text-xl font-bold font-display">{weather.temp}°C</span>
            </div>
          </div>
          
          {/* Divider */}
          <div className="w-px h-6 bg-border" />
          
          {/* Stats */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1" title="Sensación térmica">
              <Thermometer className="w-3.5 h-3.5 text-orange-400" />
              <span>{weather.feelsLike}°</span>
            </div>
            
            <div className="flex items-center gap-1" title="Humedad">
              <span className="text-blue-400">💧</span>
              <span>{weather.humidity}%</span>
            </div>
            
            {weather.precip > 0 && (
              <div className="flex items-center gap-1" title="Precipitación">
                <CloudRain className="w-3.5 h-3.5 text-blue-400" />
                <span>{weather.precip}mm</span>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default WeatherWidget;
